import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CellType } from '../../../../../enums/cellType';
import { SchoolType } from '../../../../../enums/scoreboardTypes';
import { scoreboardActions } from '../../../../../store/scoreboard/scoreboardSlice';
import { schoolTableFormatExcel, fetchStaticJson, downloadTableAsCSV } from '../../../../../utils/utils';
import ScoreboardHouse from './ScoreboardHouse/ScoreboardHouse';
import ScoreboardTable from '../../ScoreboardTable/ScoreboardTable';
import BtnComponent from '../../../../ui/buttons/BtnComponent';
import classes from './SchoolTab.module.css';

const TAB_CONFIG = {
  boardName: 'school',
  tabType: 'school',
  columns: [
    { key: 'rank', type: CellType.RANK, text: 'rank', cssClass: classes.col_rank },
    { key: 'schoolName', type: CellType.TEXT_SCHOOL_NAME, text: 'school', cssClass: classes.col_textSchoolName },
    { key: 'schoolId', type: CellType.INSTITUTION_ID, text: 'institutionId', cssClass: classes.col_institutionId },
    { key: 'city', type: CellType.TEXT_CITY, text: 'city', cssClass: classes.col_textCity },
    { key: 'ictDistrictId', type: CellType.TEXT_DISTRICT, text: 'district', cssClass: classes.col_textDistrict },
    { key: 'participantsCount', type: CellType.PARTICIPANTS, text: 'participants', cssClass: classes.col_participants },
    { key: 'studentCount', text: 'studentCount', excelOnly: true },
    { key: 'rankingScore', type: CellType.RANKING_SCORE, text: 'rankingScore', cssClass: classes.col_rankingScore },
  ],
  currentIdKey: 'school',
  houses: {
    [SchoolType.STATE]: {
      titleKey: 'stateSchools',
      endpoint: 'scoreboards/2026/14000-municipal.json',
    },
    [SchoolType.STATE_RELIGIOUS]: {
      titleKey: 'religiousSchools',
      endpoint: 'scoreboards/2026/14000-religious.json',
    },
    [SchoolType.SMALL_SCHOOLS]: {
      titleKey: 'smallSchools',
      endpoint: 'scoreboards/2026/14000-small.json',
    },
    [SchoolType.GENERAL_SCHOOLS]: {
      titleKey: 'generalSchools',
      endpoint: 'scoreboards/2026/14000-general.json',
    },
    [SchoolType.CHAREDI_GENERAL_SCHOOLS]: {
      titleKey: 'schools',
      endpoint: 'scoreboards/2026/14000-charedi-general.json',
    },
    [SchoolType.CHAREDI_SMALL_SCHOOLS]: {
      titleKey: 'smallSchools',
      endpoint: 'scoreboards/2026/14000-charedi-small.json',
    },
    [SchoolType.JERUSALEM_DISTRICT]: {
      titleKey: 'jerusalem',
      endpoint: 'scoreboards/2026/14200-district-113.json',
    },
    [SchoolType.SOUTH_DISTRICT]: {
      titleKey: 'south',
      endpoint: 'scoreboards/2026/14200-district-132.json',
    },
    [SchoolType.TEL_AVIV_DISTRICT]: {
      titleKey: 'telAviv',
      endpoint: 'scoreboards/2026/14200-district-136.json',
    },
    [SchoolType.CENTER_DISTRICT]: {
      titleKey: 'center',
      endpoint: 'scoreboards/2026/14200-district-140.json',
    },
    [SchoolType.HAIFA_DISTRICT]: {
      titleKey: 'haifa',
      endpoint: 'scoreboards/2026/14200-district-144.json',
    },
    [SchoolType.NORTH_DISTRICT]: {
      titleKey: 'north',
      endpoint: 'scoreboards/2026/14200-district-148.json',
    },
    [SchoolType.SETTLEMENT_EDUCATION_DISTRICT]: {
      titleKey: 'educationResidency',
      endpoint: 'scoreboards/2026/14200-district-167.json',
    },
    [SchoolType.JERUSALEM_CITY_DISTRICT]: {
      titleKey: 'jerusalemCity',
      endpoint: 'scoreboards/2026/14200-district-168.json',
    },
    [SchoolType.CHAREDI_DISTRICT]: {
      titleKey: 'hardy',
      endpoint: 'scoreboards/2026/14200-district-329.json',
    },
  },
};

const SchoolTab = ({ houses = [] }) => {

  const dispatch = useDispatch();
  const { texts } = useSelector((state) => state.translate);
  const { school } = useSelector((state) => state.auth);
  const { initSchoolBoard, boards, userSchoolHouseId } = useSelector((state) => state.scoreboard);
  const [activeHouseId, setActiveHouseId] = useState();

  const HouseData = boards[`${TAB_CONFIG.boardName}_${activeHouseId}`];

  useEffect(() => {
    if (!houses?.length)
      return;
    if (userSchoolHouseId !== undefined && userSchoolHouseId !== null)
      setActiveHouseId(userSchoolHouseId);

    else if (userSchoolHouseId === null)
      setActiveHouseId(houses[0].id);

  }, [userSchoolHouseId, houses]);

  useEffect(() => {
    let foundHouseId = null;
    let boardData = {};
    if (!initSchoolBoard) {
      const fetchData = async () => {
        const loadPromises = houses?.map(async (house) => {
          if (!TAB_CONFIG.houses[house.id]?.endpoint)
            return;
          try {
            const data = await fetchStaticJson(`${window.config.bucketFilesUrl}/${house?.url}`);
            boardData[`${TAB_CONFIG.boardName}_${house.id}`] = data;
            if (foundHouseId === null && userHouseRow(data))
              foundHouseId = house.id;
          }
          catch (error) {
            console.error('Error fetching school scoreboard:', error);
            boardData[`${TAB_CONFIG.boardName}_${house.id}`] = null;
          }
        });
        await Promise.all(loadPromises);
        dispatch(scoreboardActions.setSchoolBoardData({ boards: boardData, userSchoolHouseId: foundHouseId, init: true }));
      };
      fetchData();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, initSchoolBoard]);

  const handleDownloadTable = () => {
    const houseName = TAB_CONFIG.houses[activeHouseId]?.titleKey ? texts[TAB_CONFIG.houses[activeHouseId]?.titleKey] : activeHouseId;
    const { processedRows, simpleColumns } = schoolTableFormatExcel(HouseData?.rankings, TAB_CONFIG.columns, texts, `${texts?.scoreboard}-${houseName}-${new Date().toLocaleDateString('he-IL')}`);
    downloadTableAsCSV(processedRows, simpleColumns, texts, `${texts?.scoreboard}-${houseName}-${new Date().toLocaleDateString('he-IL')}`);
  };
  const userHouseRow = (house) => {
    return house?.rankings?.find((row) => String(row.schoolId) === String(school?.id));
  }
  const handleChangeHouse = (houseId) => {
    setActiveHouseId(houseId);
  }

  return (
    <div className={classes.schoolTab}>
      {houses?.length > 0 &&
        <div className={classes.tabHeader}>
          <ScoreboardHouse filters={houses?.map(house => ({ ...TAB_CONFIG.houses[house.id], id: house.id }))} activeFilterId={activeHouseId} onFilterChange={handleChangeHouse} />
          <div className={`${classes.downloadButton} ${houses.length < 4 ? classes.downloadButtonHidden : ''}`}>
            <BtnComponent type="download" text={texts.downloadTable} onClick={handleDownloadTable} />
          </div>
        </div>
      }

      <ScoreboardTable
        key={activeHouseId}
        rows={HouseData?.rankings || []}
        boardConfig={{ columns: TAB_CONFIG.columns }}
        userRow={{ key: "schoolId", value: school?.id }}
        tabType={TAB_CONFIG.tabType}
      />
    </div>
  );
};

export default SchoolTab;
